import { isComplianceSubdomain, isPurposeOfShipmentRequired } from '../../../../utils/getSubdomain';
import {
  Checkbox,
  FormHelperText,
  Grid,
  InputLabel,
  SelectChangeEvent,
  Typography,
  Card,
  CardContent,
  Box,
  Chip,
  useTheme,
  alpha,
  ButtonBase,
  Divider,
} from '@mui/material';
import React, { useState } from 'react';
import SbTextField from '../../../../components/Textfield';
import SBDropdown from '../../../../components/Dropdown';
import CreateShipmentContext from '../../../../context/CreateShipment';
import { createShipmentContextType, LengthType, ShipmentDataType } from '../../../../types/shipment';
import { defaultBoxState, SHIPMENT_MODES, SHIPMENT_UNITS, INCOTERM_OPTIONS } from '../../constants';
import { useSelector } from 'react-redux';
import { RootStateType } from '../../../../store';
import { DropdownOptionType } from '../../../../components/Dropdown/Type';
import BoxValidationError from '../BoxValidationError';
import SbButton from '../../../../components/Button';
import { SHIPMENT_TYPE } from '@Constants/Shipment';
import {
  resolveShipmentValue,
  resolveShipmentValueInEur,
  getEuComplianceRule,
  isEUCountry,
  isPlacedShipment,
} from '../../../../utils/euCompliance';
import {
  isUSDestination,
  applyCpscAutoSetForC2CSampleGift,
  applyCpscClearWhenLeavingC2C,
} from '../../../../utils/usCompliance';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';

const ShipmentTypeList = [
  { value: 'parcel', label: 'Parcel', icon: Inventory2OutlinedIcon, desc: 'Box, carton, or packaged goods' },
  { value: 'document', label: 'Document', icon: DescriptionOutlinedIcon, desc: 'Papers, legal files, or envelopes' },
];

const getShipmentTypeList = (name: string) => {
  if (name === 'Cargo Solutions Network') {
    return ShipmentTypeList.filter(item => item.value === 'parcel');
  }
  return ShipmentTypeList;
};

interface ShipmentTypeDetailsProps {
  pendingRequirements?: { label: string; boxIndex?: number }[];
}

const ShipmentTypeDetails: React.FC<ShipmentTypeDetailsProps> = (): JSX.Element => {
  const theme = useTheme();
  const context = React.useContext(CreateShipmentContext) as createShipmentContextType;
  const {
    shipmentType,
    setShipmentType,
    packageData,
    setPackageData,
    boxes,
    setBoxes,
    extraDetails,
    setExtraDetails,
    totalShipmentValue,
    consignee,
  } = context;

  const { currencyList } = useSelector((state: RootStateType) => state?.global);
  const orgDefaultCurrency = useSelector((state: RootStateType) => state.orgSetup?.organizationData?.currency?.name);
  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const { name = '' } = organizationData || {};
  const shipmentSetting = useSelector((state: RootStateType) => state.orgSetup.shipmentSetting);
  const disableInsurance = !shipmentSetting?.insuranceCharge?.charge;
  const disablePackaging = !shipmentSetting?.packagingCharge?.charge;
  const { type, invoiceNumber, numberOfBoxes, currency, unit, shipmentMode, referenceNumber } = shipmentType;

  const [openBoxValidationModal, setOpenBoxValidationModal] = useState(false);
  const [vatTouched, setVatTouched] = useState(false);

  React.useEffect(() => {
    const handleValidate = () => setVatTouched(true);
    window.addEventListener('validate-shipment-details', handleValidate);
    return () => window.removeEventListener('validate-shipment-details', handleValidate);
  }, []);

  // Destination checks
  const destCountry = (consignee?.country as any)?.name || consignee?.country || '';
  const isDestEU = isEUCountry(destCountry);
  const isDestUS = isUSDestination(destCountry);

  // Active shipment mode and incoterm
  const currentMode = (shipmentMode || '').toLowerCase();
  const isB2B = currentMode === 'b2b';
  const isDocument = (shipmentType?.type || '').toLowerCase() === 'document';

  const isComplianceActive = isComplianceSubdomain();
  // Recipient VAT / Tax ID — visible only when compliance subdomain + B2B + destination EU AND not document; required only after placed
  const isVatVisible = isComplianceActive && !isDocument && isB2B && isDestEU;
  const isVatRequired = isComplianceActive && !isDocument && isPlacedShipment() && isB2B && isDestEU;

  // IOSS Number — visible for all EU destinations; never required
  const isIossVisible = isDestEU;

  // Reactive reset when destination leaves EU, mode leaves B2B, or type becomes Document
  React.useEffect(() => {
    if (!isComplianceActive || !isDestEU || !isB2B || isDocument) {
      if (shipmentType.recipientVatTaxId) {
        setShipmentType({
          ...shipmentType,
          recipientVatTaxId: '',
        });
      }
      if (extraDetails.recipientVatTaxId) {
        setExtraDetails({ ...extraDetails, recipientVatTaxId: '' });
      }
      setVatTouched(false);
    }
  }, [isDestEU, isB2B, isDocument, isComplianceActive]);

  React.useEffect(() => {
    if (!isDestEU) {
      if (shipmentType.iossNumber) {
        setShipmentType({
          ...shipmentType,
          iossNumber: '',
        });
      }
      if (extraDetails.iossNumber) {
        setExtraDetails({ ...extraDetails, iossNumber: '' });
      }
    }
  }, [isDestEU]);

  // Purpose of Shipment handler — C2C sample/gift auto-sets beingSold = false for US destination
  const handlePurposeChange = ([val]: string[]) => {
    const selectedVal = val || '';
    setExtraDetails({ ...extraDetails, purposeOfShipment: selectedVal });
    setShipmentType({ ...shipmentType, purposeOfShipment: selectedVal });
    setPackageData({ ...packageData, purposeOfShipment: selectedVal });
    const cleanVal = selectedVal.trim().toLowerCase();
    const isC2C = (shipmentMode || shipmentType.shipmentMode || '').trim().toLowerCase() === 'c2c';
    if (isDestUS && isC2C && (cleanVal === 'sample' || cleanVal === 'gift')) {
      if (boxes && boxes.length) {
        const { updatedBoxes, didUpdate } = applyCpscAutoSetForC2CSampleGift(
          boxes,
          shipmentMode || shipmentType.shipmentMode,
          selectedVal,
          destCountry,
        );
        if (didUpdate) {
          setBoxes(updatedBoxes);
        }
      }
    } else if (isDestUS && isC2C && boxes && boxes.length) {
      const { updatedBoxes, didUpdate } = applyCpscClearWhenLeavingC2C(boxes);
      if (didUpdate) {
        setBoxes(updatedBoxes);
      }
    }
  };

  const currentIncoterm = shipmentType.incoterm || extraDetails.incoterm || 'FOB';
  const isCIF = currentIncoterm === 'CIF';
  const isCNF = currentIncoterm === 'CNF';
  const hasFreightInsuranceSection = isCIF || isCNF;

  // Resolved shipment value for reference breakdown
  const resolvedShipmentValue = resolveShipmentValue(boxes, totalShipmentValue);
  const freightAmount =
    shipmentType.freight !== undefined && shipmentType.freight !== ''
      ? shipmentType.freight
      : extraDetails.freight ?? '';
  const insuranceAmount =
    shipmentType.freightInsurance !== undefined && shipmentType.freightInsurance !== ''
      ? shipmentType.freightInsurance
      : extraDetails.freightInsurance ?? '';

  const numericFreight = Number(freightAmount) || 0;
  const numericInsurance = Number(insuranceAmount) || 0;
  const totalBreakupValue = resolvedShipmentValue + numericFreight + numericInsurance;

  function addBoxes(event: React.ChangeEvent<HTMLInputElement>) {
    const { value } = event.target;
    const boxCount = parseInt(value);
    const boxesTemp = [...boxes];
    if (boxes.length > boxCount) {
      setOpenBoxValidationModal(true);
    } else if (boxes.length < boxCount) {
      for (let i = 0; i < boxCount - boxes.length; i++) {
        const box = structuredClone(defaultBoxState);
        box['id'] = boxesTemp.length.toString();
        boxesTemp.push(box);
      }
    }
    setBoxes(boxesTemp);
  }

  function handleChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { name: fieldName, value } = event.target;
    const data = { ...shipmentType };
    if (fieldName === 'type' && value === 'document' && boxes.length > 1) {
      setOpenBoxValidationModal(true);
    } else if (
      fieldName === 'invoiceNumber' ||
      fieldName === 'numberOfBoxes' ||
      fieldName === 'referenceNumber' ||
      fieldName === 'type'
    ) {
      (data as any)[fieldName] = value;
    }
    setShipmentType({ ...data });
    if (fieldName === 'invoiceNumber' && packageData) {
      setPackageData({ ...packageData, invoiceNumber: value });
    }
  }

  function handleDropdownChange(
    _selected: Array<string>,
    _selectedObj: DropdownOptionType[],
    event: SelectChangeEvent<string>,
    _child: React.ReactNode,
  ) {
    const { name: fieldName, value } = event.target;
    const data: ShipmentDataType = { ...shipmentType };
    if (
      fieldName === 'shipmentMode' ||
      fieldName === 'currency' ||
      fieldName === 'type' ||
      fieldName === 'dutyCharge'
    ) {
      if (fieldName === 'dutyCharge') {
        const currentCharge = typeof shipmentType.dutyCharge === 'object' ? shipmentType.dutyCharge.charge || 0 : 0;
        data[fieldName] = {
          type: value,
          charge: currentCharge,
        };
      } else {
        (data as any)[fieldName] = value;
      }
    } else if (fieldName === 'unit') {
      data[fieldName] = value as LengthType;
    }

    // Reset conditional fields on shipmentMode change
    if (fieldName === 'shipmentMode') {
      const newMode = (value || '').toUpperCase();
      const isNewB2B = newMode === 'B2B';
      const isNewC2C = newMode === 'C2C';

      setVatTouched(false);

      const cleanPurpose = (extraDetails?.purposeOfShipment || '').trim().toLowerCase();
      if (isDestUS && isNewC2C && (cleanPurpose === 'sample' || cleanPurpose === 'gift') && boxes?.length) {
        const { updatedBoxes, didUpdate } = applyCpscAutoSetForC2CSampleGift(
          boxes,
          newMode,
          extraDetails?.purposeOfShipment,
          destCountry,
        );
        if (didUpdate) {
          setBoxes(updatedBoxes);
        }
      } else if (isDestUS && !isNewC2C && boxes?.length) {
        const { updatedBoxes, didUpdate } = applyCpscClearWhenLeavingC2C(boxes);
        if (didUpdate) {
          setBoxes(updatedBoxes);
        }
      }

      const updatedExtra = { ...extraDetails };
      if (!isNewB2B || !isDestEU) {
        data.recipientVatTaxId = '';
        updatedExtra.recipientVatTaxId = '';
      }
      setExtraDetails(updatedExtra);

      // Check if EU compliance fields should be cleared
      const valEur = resolveShipmentValueInEur(boxes, data.currency || 'INR', totalShipmentValue);
      const rule = getEuComplianceRule({
        destCountry,
        shipmentMode: newMode,
        shipmentValueEur: valEur,
        purposeOfShipment: extraDetails?.purposeOfShipment,
        isDocument: (data.type || shipmentType.type || '').toLowerCase() === 'document',
      });
      if (!rule.showEuComplianceFields && boxes?.length) {
        const updatedBoxes = boxes.map((b: any) => ({
          ...b,
          products: (b.products || []).map((p: any) => ({
            ...p,
            merchantProductId: '',
            standardManufacturerProductId: '',
            nonStandardManufacturerProductId: '',
          })),
        }));
        setBoxes(updatedBoxes);
      }
    }

    setShipmentType({ ...data });
  }

  const handleIncotermChange = (selected: string[]) => {
    const val = selected[0] || 'FOB';
    const updatedShipment = { ...shipmentType, incoterm: val };
    const updatedExtra = { ...extraDetails, incoterm: val };

    if (val === 'FOB') {
      updatedShipment.freight = '';
      updatedShipment.freightInsurance = '';
      updatedExtra.freight = '';
      updatedExtra.freightInsurance = '';
    } else if (val === 'CNF') {
      updatedShipment.freightInsurance = '';
      updatedExtra.freightInsurance = '';
    }

    setShipmentType(updatedShipment);
    setExtraDetails(updatedExtra);
  };

  const handleExtraDetailFieldChange = (
    field: 'iossNumber' | 'recipientVatTaxId' | 'freight' | 'freightInsurance' | 'ior',
    value: string,
  ) => {
    setShipmentType({ ...shipmentType, [field]: value });
    setExtraDetails({ ...extraDetails, [field]: value });
  };

  const availableShipmentTypes = getShipmentTypeList(name);

  return (
    <>
      <BoxValidationError open={openBoxValidationModal} onClose={() => setOpenBoxValidationModal(false)} />
      <Card
        elevation={0}
        sx={{
          width: '100%',
          borderRadius: '14px',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
          bgcolor: theme.palette.background.paper,
        }}
      >
        {/* Card Header */}
        <Box
          sx={{
            p: { xs: 1.5, sm: 2 },
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
            bgcolor: alpha(theme.palette.primary.main, 0.02),
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <TuneOutlinedIcon />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'primary.main' }}>
                  Shipment Specifications
                </Typography>
                <Chip
                  label={type === 'document' ? 'Document' : 'Parcel'}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                  }}
                />
              </Box>
              <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'text.secondary' }}>
                Define shipment type, incoterms, measurement units, and invoice declarations
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Card Body */}
        <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
          {/* 1. Shipment Type Segmented Toggle */}
          <Box sx={{ mb: 2.5 }}>
            <InputLabel
              required
              sx={{
                mb: '8px',
                color: 'common.black',
                fontSize: '14px',
                fontWeight: 500,
                textTransform: 'none',
                letterSpacing: '0.3px',
                display: 'block',
                '&.Mui-required .MuiFormLabel-asterisk': {
                  color: 'inherit',
                  marginLeft: '4px',
                },
              }}
            >
              Select Shipment Type
            </InputLabel>
            <Grid container spacing={2}>
              {availableShipmentTypes.map(item => {
                const isSelected = type === item.value;
                const IconComponent = item.icon;
                return (
                  <Grid item xs={12} sm={6} md={3} key={item.value}>
                    <ButtonBase
                      onClick={() => {
                        const syntheticEvent = {
                          target: { name: 'type', value: item.value },
                        } as React.ChangeEvent<HTMLInputElement>;
                        handleChange(syntheticEvent);
                      }}
                      sx={{
                        width: '100%',
                        p: 1.5,
                        borderRadius: '12px',
                        border: `1.5px solid ${
                          isSelected ? theme.palette.primary.main : alpha(theme.palette.divider, 0.15)
                        }`,
                        bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.03) : theme.palette.background.paper,
                        boxShadow: isSelected
                          ? `0 0 0 1px ${theme.palette.primary.main}, 0 2px 8px ${alpha(
                              theme.palette.primary.main,
                              0.08,
                            )}`
                          : '0 1px 2px rgba(0,0,0,0.02)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: 1.5,
                        textAlign: 'left',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: isSelected ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.4),
                          bgcolor: alpha(theme.palette.primary.main, 0.02),
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: '8px',
                          bgcolor: isSelected ? theme.palette.primary.main : alpha(theme.palette.text.secondary, 0.08),
                          color: isSelected ? theme.palette.common.white : 'text.secondary',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <IconComponent sx={{ fontSize: 20 }} />
                      </Box>
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: isSelected ? 'primary.main' : 'text.primary',
                          }}
                        >
                          {item.label}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#475467', display: 'block' }}>
                          {item.desc}
                        </Typography>
                      </Box>
                    </ButtonBase>
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          {/* 2. Key Shipment Parameters */}
          <Grid container spacing={2}>
            {/* Row 1: Shipment Mode *, Preferred Unit *, No. of Boxes * */}
            <Grid item xs={12} sm={6} md={4}>
              <SBDropdown
                required
                label="Shipment Mode"
                id="shipmentMode"
                name="shipmentMode"
                placeholder="Select Mode"
                options={SHIPMENT_MODES}
                defaultValue={shipmentMode ? [shipmentMode] : []}
                value={shipmentMode ? [shipmentMode] : []}
                onChange={handleDropdownChange}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SBDropdown
                required
                id="company-country-id"
                label="Preferred Unit"
                name="unit"
                placeholder="kg-cm"
                options={SHIPMENT_UNITS}
                value={unit ? [unit] : []}
                onChange={handleDropdownChange}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <SbTextField
                placeholder="1"
                label="No. of Boxes"
                onChange={event => {
                  event.target.value = `${Math.floor(Number(event.target.value))}`;
                  handleChange(event);
                }}
                name="numberOfBoxes"
                value={numberOfBoxes}
                required
                onBlur={addBoxes}
                type="number"
              />
            </Grid>

            {/* Row 2: Currency *, Purpose of Shipment, Reference Number, Invoice Number */}
            <Grid item xs={12} sm={6} md={3}>
              <SBDropdown
                id="currency-select"
                label="Currency"
                placeholder="Select Currency"
                options={currencyList}
                required
                defaultValue={orgDefaultCurrency ? [orgDefaultCurrency] : []}
                name="currency"
                value={currency ? [currency] : []}
                onChange={handleDropdownChange}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SBDropdown
                id="purposeOfShipment"
                placeholder="Select Purpose"
                label="Purpose Of Shipment"
                required={isPurposeOfShipmentRequired(shipmentType)}
                name="purposeOfShipment"
                options={SHIPMENT_TYPE}
                value={
                  extraDetails?.purposeOfShipment ||
                  shipmentType?.purposeOfShipment ||
                  (packageData as any)?.purposeOfShipment
                    ? [
                        extraDetails?.purposeOfShipment ||
                          shipmentType?.purposeOfShipment ||
                          (packageData as any)?.purposeOfShipment,
                      ]
                    : []
                }
                onChange={handlePurposeChange}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SbTextField
                placeholder="e.g. REF-10293"
                label="Reference Number"
                value={referenceNumber}
                onChange={handleChange}
                name="referenceNumber"
              />
              <FormHelperText sx={{ mx: 0.5, mt: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                Shipment can be tracked with ref. no.
              </FormHelperText>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <SbTextField
                placeholder="e.g. INV-9021"
                label="Invoice Number"
                value={invoiceNumber}
                onChange={handleChange}
                name="invoiceNumber"
              />
              <FormHelperText sx={{ mx: 0.5, mt: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                Auto-generated if left blank
              </FormHelperText>
            </Grid>

            {/* Row 3: Mode-Driven Identification & Compliance Fields */}
            {isIossVisible && (
              <Grid item xs={12} sm={6} md={3}>
                <SbTextField
                  label="IOSS Number"
                  name="iossNumber"
                  placeholder="Enter IOSS Number"
                  value={shipmentType.iossNumber || extraDetails.iossNumber || ''}
                  onChange={e => handleExtraDetailFieldChange('iossNumber', e.target.value)}
                />
              </Grid>
            )}

            {isVatVisible && (
              <Grid item xs={12} sm={6} md={3}>
                <SbTextField
                  required={isVatRequired}
                  label={`Recipient VAT / Tax ID${isVatRequired ? ' *' : ''}`}
                  name="recipientVatTaxId"
                  placeholder="Enter VAT / Tax ID"
                  value={shipmentType.recipientVatTaxId || extraDetails.recipientVatTaxId || ''}
                  onChange={e => handleExtraDetailFieldChange('recipientVatTaxId', e.target.value)}
                  onBlur={() => setVatTouched(true)}
                  error={
                    isVatRequired &&
                    vatTouched &&
                    !(shipmentType.recipientVatTaxId || extraDetails.recipientVatTaxId)?.trim()
                      ? 'Recipient VAT / Tax ID is required for B2B shipments'
                      : ''
                  }
                />
                <FormHelperText sx={{ mx: 0.5, mt: 0.5, color: 'text.secondary', fontSize: '0.75rem' }}>
                  {isVatRequired ? 'Required for B2B commercial shipments' : ''}
                </FormHelperText>
              </Grid>
            )}

            {/* IOR / EORI No. — ALWAYS VISIBLE for every shipment */}
            <Grid item xs={12} sm={6} md={3}>
              <SbTextField
                label="IOR/EORI No."
                name="ior"
                placeholder="Enter IOR or EORI No."
                value={extraDetails.ior || ''}
                onChange={val => handleExtraDetailFieldChange('ior', val.target.value)}
              />
            </Grid>
          </Grid>

          {/* Value-Added Services */}
          {name !== 'Cargo Solutions Network' && (
            <Box
              sx={{
                mt: 2.5,
                pt: 2,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                width: '100%',
              }}
            >
              <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'text.primary', mb: 1.5 }}>
                Value-Added Services
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: { xs: 2, sm: 3, md: 4 },
                  width: '100%',
                }}
              >
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    flexWrap: 'nowrap',
                    cursor: disableInsurance ? 'not-allowed' : 'pointer',
                    opacity: disableInsurance ? 0.6 : 1,
                  }}
                >
                  <Checkbox
                    name="insurance"
                    id="insurance"
                    sx={{ mr: 0.5, p: 0.5 }}
                    checked={!!packageData.insurance}
                    onChange={e =>
                      setPackageData({
                        ...packageData,
                        insurance: e.target.checked,
                      })
                    }
                    inputProps={{ 'aria-label': 'controlled' }}
                    disabled={disableInsurance}
                  />
                  <img
                    src="/images/Boxes/Insurance.svg"
                    alt="insurance"
                    style={{ width: 22, height: 22, marginRight: 6 }}
                  />
                  <Typography
                    component="label"
                    htmlFor="insurance"
                    sx={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: 'text.primary',
                      cursor: disableInsurance ? 'not-allowed' : 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    Insure Your Shipment
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    flexWrap: 'nowrap',
                    cursor: disablePackaging ? 'not-allowed' : 'pointer',
                    opacity: disablePackaging ? 0.6 : 1,
                  }}
                >
                  <Checkbox
                    name="packaging"
                    id="packaging"
                    sx={{ mr: 0.5, p: 0.5 }}
                    checked={!!packageData.packaging}
                    onChange={e =>
                      setPackageData({
                        ...packageData,
                        packaging: e.target.checked,
                      })
                    }
                    inputProps={{ 'aria-label': 'controlled' }}
                    disabled={disablePackaging}
                  />
                  <img src="/images/Boxes/Box.svg" alt="packaging" style={{ width: 22, height: 22, marginRight: 6 }} />
                  <Typography
                    component="label"
                    htmlFor="packaging"
                    sx={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: 'text.primary',
                      cursor: disablePackaging ? 'not-allowed' : 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    Add Packaging
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* 2. Incoterms & Commercial Terms Section — visible on this branch */}
      {true && (
        <Card
          elevation={0}
          sx={{
            width: '100%',
            mt: 3,
            borderRadius: '14px',
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.04), 0 2px 8px -2px rgba(0, 0, 0, 0.03)',
            bgcolor: theme.palette.background.paper,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              p: { xs: 1.5, sm: 2 },
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
              bgcolor: alpha(theme.palette.primary.main, 0.02),
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <PaymentsOutlinedIcon />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'primary.main' }}>
                  Incoterms & Commercial Terms
                </Typography>
                <Chip
                  label={currentIncoterm}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                  }}
                />
              </Box>
              <Typography sx={{ fontSize: '14px', fontWeight: 400, color: 'text.secondary' }}>
                Specify the international commercial terms and declared freight & insurance charges
              </Typography>
            </Box>
          </Box>

          <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
            <Box sx={{ mb: hasFreightInsuranceSection ? 2.5 : 0 }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary', mb: 1 }}>
                Select Incoterm *
              </Typography>
              <Box
                sx={{
                  display: 'inline-flex',
                  p: 0.5,
                  bgcolor: alpha(theme.palette.divider, 0.08),
                  borderRadius: '10px',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                {INCOTERM_OPTIONS.map(opt => {
                  const isSelected = currentIncoterm === opt.value;
                  const formattedLabel = opt.display.replace(' - ', ' — ');
                  return (
                    <SbButton
                      key={opt.value}
                      variant={isSelected ? 'contained' : 'text'}
                      onClick={() => handleIncotermChange([opt.value])}
                      sx={{
                        px: 2.5,
                        py: 1,
                        borderRadius: '8px',
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        bgcolor: isSelected ? 'primary.main' : 'transparent',
                        color: isSelected ? 'common.white' : 'text.primary',
                        boxShadow: isSelected ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
                        '&:hover': {
                          bgcolor: isSelected ? 'primary.dark' : alpha(theme.palette.action.hover, 0.1),
                        },
                      }}
                    >
                      {formattedLabel}
                    </SbButton>
                  );
                })}
              </Box>
            </Box>

            {/* Freight & Insurance Section (Conditional on CIF / CNF) */}
            {hasFreightInsuranceSection && (
              <Box
                sx={{
                  mt: 2.5,
                  p: { xs: 2, sm: 2.5 },
                  borderRadius: '12px',
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  bgcolor: alpha(theme.palette.primary.main, 0.02),
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <PaymentsOutlinedIcon color="primary" fontSize="small" />
                  <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'primary.main' }}>
                    Freight & Insurance ({currentIncoterm})
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', display: 'block', mb: 2, fontSize: '14px' }}
                >
                  {isCIF
                    ? 'CIF Incoterm includes both Freight and Insurance charges in the declaration.'
                    : 'CNF Incoterm includes Freight charges; Insurance is optional.'}
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <SbTextField
                      required
                      type="number"
                      label={`Freight (${currency || 'INR'})`}
                      placeholder="Enter freight amount"
                      name="freight"
                      value={freightAmount}
                      onChange={e => handleExtraDetailFieldChange('freight', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <SbTextField
                      required={isCIF}
                      type="number"
                      label={`Insurance (${currency || 'INR'})${isCIF ? '' : ' (Optional)'}`}
                      placeholder="Enter insurance amount"
                      name="freightInsurance"
                      value={insuranceAmount}
                      onChange={e => handleExtraDetailFieldChange('freightInsurance', e.target.value)}
                    />
                  </Grid>
                </Grid>

                {/* Price Breakup Sub-section */}
                <Box
                  sx={{
                    mt: 2.5,
                    p: 2,
                    borderRadius: '10px',
                    bgcolor: theme.palette.background.paper,
                    border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                  }}
                >
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary', mb: 1.5 }}>
                    Price Breakup
                  </Typography>
                  <Divider sx={{ mb: 2, borderColor: alpha(theme.palette.divider, 0.08) }} />
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.secondary', display: 'block' }}>
                        Shipment Value (Goods)
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, fontSize: '14px', color: 'text.primary', mt: 0.5 }}
                      >
                        {currency || 'INR'} {resolvedShipmentValue.toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.secondary', display: 'block' }}>
                        Freight Amount
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          fontSize: '14px',
                          color: numericFreight > 0 ? 'text.primary' : 'text.secondary',
                          mt: 0.5,
                        }}
                      >
                        {currency || 'INR'} {numericFreight.toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.secondary', display: 'block' }}>
                        Insurance Amount
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          fontSize: '14px',
                          color: numericInsurance > 0 ? 'text.primary' : 'text.secondary',
                          mt: 0.5,
                        }}
                      >
                        {currency || 'INR'} {numericInsurance.toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'primary.main', display: 'block' }}>
                        Total ({currentIncoterm})
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: 'primary.main', mt: 0.5 }}>
                        {currency || 'INR'} {totalBreakupValue.toFixed(2)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default ShipmentTypeDetails;
